import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article.slug, article.categorySlug) : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-6">你访问的文章可能已被移除或链接有误。</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-lg transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <Breadcrumb
          items={[
            { label: article.categoryName, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-medium"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 text-sm text-foreground-400">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-foreground-700">{article.author}</p>
                  <p className="text-xs">
                    {article.publishDate} · {article.readTime} 分钟阅读
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[300px] md:h-[400px] object-cover object-top"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-950 prose-strong:font-semibold
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700
                prose-img:rounded-xl prose-img:mx-auto"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            <hr className="my-12 border-background-200" />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author}</p>
                  <p className="text-xs text-foreground-400">男子汉宝藏签约作者</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">分享：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-colors" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="border-t border-background-200 pt-10">
                <h3 className="font-heading font-bold text-xl text-foreground-950 mb-6">相关推荐</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="h-36 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="font-heading font-medium text-sm text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                          {rel.title}
                        </h4>
                        <span className="text-xs text-foreground-400">{rel.readTime} 分钟阅读</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}